import React, { useEffect, useState } from 'react';

import { Divider, InputNumber } from 'antd';

import { isJobNonExtEvent, isCastingEvent, isCastingOrJobEvent, isExtEvent } from '@libs/EventTypes';
import { ShowMultiTimePeriod, showDateNoTime, showPeriod } from '@libs/NwMoment';
import { getProjectCustomerIds } from '@libs/ProjectUtils';

import { useProjectContext } from '@contexts/ProjectContext';

import ModelEventTypeTag from '@components/EventDetails/ModelEventTypeTag';
import PreviousEventsButton from '@components/EventDrawer/EventDrawerUi/PreviousEventsButton';
import Switch from '@components/Gui/CustomSwitch';
import ModelsConfirmedInfo from '@components/Gui/ModelsConfirmedInfo';
import { NwLinkButton } from '@components/Gui/NwButton';
import { ModelSelectorContainer } from '@components/Project/ProjectTransactions/Transactions/ProjectTransactionsUI';

const getEventName = (eventName) => {
    if (!eventName) return eventName;
    return eventName.length > 40 ? `${eventName.substring(0, 40)}...` : eventName;
};

const ModelSelectorByWorkDays = ({ typeQuantity, models, updateModels, updateGroupWorkDays, commasmothag = false }) => {
    const [showAllBookingEvents, setShowAllBookingEvents] = useState(false);
    const [showCancelledDates, setShowCancelledDates] = useState(false);
    const [groupWorkDays, setGroupWorkDays] = useState(false);
    const [allHours, setAllHours] = useState(8);
    const [allUnits, setAllUnits] = useState(1);

    useEffect(() => {
        setGroupWorkDays(false);
    }, [typeQuantity]);

    const { projectData } = useProjectContext();
    const clientIDs = getProjectCustomerIds(projectData);

    const handleChangeGroupModelJobEvents = (active, groupModelIndex) => {
        let newModels = [...models];
        newModels[groupModelIndex].Active = active;
        if (active) {
            for (let i = 0; i < newModels[groupModelIndex].EventDates.length; i++) {
                newModels[groupModelIndex].EventDates[i].Active = false;
            }
        }
        updateModels(newModels);
    };

    const handleChangeGroupModelJobEventsQuantity = (quantity, groupModelIndex) => {
        let newModels = [...models];
        newModels[groupModelIndex].Quantity = quantity;
        updateModels(newModels);
    };

    const handleChangeModelEventDate = (active, groupModelIndex, modelDate) => {
        let newModels = [...models];
        const modelDateIndex = newModels[groupModelIndex].EventDates.findIndex(
            (eventDate) => eventDate.EventDateID === modelDate.EventDateID
        );
        newModels[groupModelIndex].EventDates[modelDateIndex].Active = active;
        updateModels(newModels);
    };

    const handleChangeModelEventDateQuantity = (quantity, groupModelIndex, modelDate) => {
        let newModels = [...models];
        const modelDateIndex = newModels[groupModelIndex].EventDates.findIndex(
            (eventDate) => eventDate.EventDateID === modelDate.EventDateID
        );
        newModels[groupModelIndex].EventDates[modelDateIndex].Quantity = quantity;
        updateModels(newModels);
    };

    const handleChangeModelQuantity = (quantity, groupModelIndex) => {
        let newModels = [...models];
        newModels[groupModelIndex].Quantity = quantity;
        updateModels(newModels);
    };

    const handleSetAllHours = (quantity) => {
        let newModels = [...models];
        for (let modelIndex = 0; modelIndex < newModels.length; modelIndex++) {
            for (let i = 0; i < newModels[modelIndex].EventDates.length; i++) {
                newModels[modelIndex].EventDates[i].Quantity = quantity;
            }
        }
        updateModels(newModels);
    };

    const handleSetAllUnits = (quantity) => {
        let newModels = [...models];
        for (let modelIndex = 0; modelIndex < newModels.length; modelIndex++) {
            newModels[modelIndex].Quantity = quantity;
        }
        updateModels(newModels);
    };

    const handleShowAllBookingEvents = (value) => {
        if (value) {
            setGroupWorkDays(false);
        }
        setShowAllBookingEvents(value);
    };

    const handleShowCancelledDates = (value) => {
        setShowCancelledDates(value);
    };

    const getEventCount = (groupedModel) => {
        const eventFilter = commasmothag ? isExtEvent : isJobNonExtEvent;
        const confirmedJobEvents = groupedModel.EventDates.filter(
            (eventDate) => eventFilter(eventDate.EventType) && eventDate.OptionNumber === 10 && !eventDate.Cancel
        );
        const optionEvents = groupedModel.EventDates.filter(
            (eventDate) => eventFilter(eventDate.EventType) && !eventDate.Cancel && eventDate.OptionNumber < 10
        );
        const cancelledEvents = groupedModel.EventDates.filter(
            (eventDate) => eventFilter(eventDate.EventType) && eventDate.Cancel
        );
        return `${confirmedJobEvents.length} confirmations, ${optionEvents.length} options, ${cancelledEvents.length} cancelled events`;
    };

    const activateAllModel = (modelID) => {
        let newModels = [...models];
        const modelIndex = newModels.findIndex((model) => model.ModelID === modelID);
        for (let i = 0; i < newModels[modelIndex].EventDates.length; i++) {
            const eventDate = newModels[modelIndex].EventDates[i];
            let shouldActivate;
            if (commasmothag) {
                shouldActivate = isExtEvent(eventDate.EventType);
            } else {
                shouldActivate = showAllBookingEvents
                    ? isCastingOrJobEvent(eventDate.EventType)
                    : isJobNonExtEvent(eventDate.EventType);
            }
            if (shouldActivate) {
                newModels[modelIndex].EventDates[i].Active = true;
            }
        }
        updateModels(newModels);
    };

    const deactivateAllModel = (modelID) => {
        let newModels = [...models];
        const modelIndex = newModels.findIndex((model) => model.ModelID === modelID);
        for (let i = 0; i < newModels[modelIndex].EventDates.length; i++) {
            newModels[modelIndex].EventDates[i].Active = false;
        }
        updateModels(newModels);
    };

    const ModelWithPreviousEvents = ({ model, isCancelled, isConfirmed }) => {
        return (
            <div className="model-and-previous-events">
                <div className={`model-name${isCancelled ? ' is-cancelled' : ''}${isConfirmed ? ' is-confirmed' : ''}`}>
                    {model.ModelName}
                </div>
                <PreviousEventsButton modelID={model.ModelID} clientIDs={clientIDs} />
            </div>
        );
    };

    const onGroupWorkDaysChange = (value) => {
        setGroupWorkDays(value);
        if (updateGroupWorkDays) {
            updateGroupWorkDays(typeQuantity, value);
        }
    };

    // Get model dates based on mode
    const getModelDates = (groupedModel) => {
        if (commasmothag) {
            return groupedModel.EventDates.filter((eventDate) => isExtEvent(eventDate.EventType));
        }
        const jobEvents = groupedModel.EventDates.filter((eventDate) => isJobNonExtEvent(eventDate.EventType));
        const castingEvents = groupedModel.EventDates.filter((eventDate) => isCastingEvent(eventDate.EventType));
        return showAllBookingEvents ? [...jobEvents, ...castingEvents] : [...jobEvents];
    };

    // Check if model should be hidden based on cancelled state
    const shouldHideModel = (groupedModel) => {
        return !showCancelledDates && groupedModel.AlwaysCancelled;
    };

    // Check if date should be hidden based on cancelled state
    const shouldHideDate = (modelDate) => {
        return !showCancelledDates && modelDate.Cancel;
    };

    return (
        <ModelSelectorContainer>
            <h1>Models in current project</h1>
            <div className="selection-bar">
                {!commasmothag && (
                    <Switch text="Show castings" checked={showAllBookingEvents} onChange={handleShowAllBookingEvents} />
                )}
                <div className="show-cancelled-dates">
                    <Switch
                        text="Show cancelled dates/models"
                        checked={showCancelledDates}
                        onChange={handleShowCancelledDates}
                    />
                </div>
                {typeQuantity === 'Hours' && (
                    <div className="set-hours">
                        set{' '}
                        <InputNumber
                            value={allHours}
                            min={0.5}
                            max={23.5}
                            step={0.5}
                            onChange={(value) => setAllHours(value)}
                        />{' '}
                        hour(s) for all models{' '}
                        <NwLinkButton size="small" label="apply" onClick={() => handleSetAllHours(allHours)} />
                    </div>
                )}
                {typeQuantity === 'Units' && (
                    <div className="set-units">
                        set <InputNumber value={allUnits} step={1} onChange={(value) => setAllUnits(value)} /> unit(s)
                        for all models{' '}
                        <NwLinkButton size="small" label="apply" onClick={() => handleSetAllUnits(allUnits)} />
                    </div>
                )}
                {(typeQuantity === 'Days' || typeQuantity === 'HalfDays') && (
                    <div className="group-days">
                        <Switch
                            text="Group work days in a single fee"
                            disabled={!commasmothag && showAllBookingEvents}
                            checked={groupWorkDays}
                            onChange={onGroupWorkDaysChange}
                        />
                    </div>
                )}
            </div>
            <div className="models">
                {(typeQuantity === 'Units' || (typeQuantity === 'Flat' && !commasmothag)) && <ModelsConfirmedInfo />}
                {models.map((groupedModel, index) => {
                    const modelDates = getModelDates(groupedModel);

                    if (modelDates.length === 0) return null;
                    if (shouldHideModel(groupedModel)) return null;

                    return (
                        <div key={groupedModel.ModelID}>
                            {/* Units mode */}
                            {typeQuantity === 'Units' && (
                                <div
                                    className={`model${groupedModel.AlwaysCancelled ? ' is-cancelled' : ''}${groupedModel.HasConfirmation ? ' is-confirmed' : ''}`}
                                >
                                    <Switch
                                        text={
                                            <ModelWithPreviousEvents
                                                model={groupedModel}
                                                isCancelled={groupedModel.AlwaysCancelled}
                                                isConfirmed={groupedModel.HasConfirmation}
                                            />
                                        }
                                        checked={groupedModel.Active}
                                        onChange={(value) => handleChangeGroupModelJobEvents(value, index)}
                                    />
                                    {groupedModel.Active && (
                                        <div className="model-units-quantity">
                                            <InputNumber
                                                value={groupedModel.Quantity}
                                                min={1}
                                                step={1}
                                                onChange={(value) => handleChangeModelQuantity(value, index)}
                                            />{' '}
                                            items
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Flat mode */}
                            {typeQuantity === 'Flat' && (
                                <div
                                    className={`model${groupedModel.AlwaysCancelled ? ' is-cancelled' : ''}${groupedModel.HasConfirmation ? ' is-confirmed' : ''}`}
                                >
                                    <Switch
                                        text={
                                            <ModelWithPreviousEvents
                                                model={groupedModel}
                                                isCancelled={groupedModel.AlwaysCancelled}
                                                isConfirmed={groupedModel.HasConfirmation}
                                            />
                                        }
                                        checked={groupedModel.Active}
                                        onChange={(value) => handleChangeGroupModelJobEvents(value, index)}
                                    />
                                </div>
                            )}

                            {/* Days/HalfDays mode - grouped */}
                            {(typeQuantity === 'Days' || typeQuantity === 'HalfDays') && groupWorkDays && (
                                <div className="model">
                                    <Switch
                                        text={
                                            <ModelWithPreviousEvents
                                                model={groupedModel}
                                                isCancelled={groupedModel.AlwaysCancelled}
                                                isConfirmed={groupedModel.HasConfirmation}
                                            />
                                        }
                                        checked={groupedModel.Active}
                                        onChange={(value) => handleChangeGroupModelJobEvents(value, index)}
                                    />
                                    {groupedModel.Active && (
                                        <div className="event-details">
                                            <div className="event-details-count">
                                                <div>{getEventCount(groupedModel)}</div>
                                            </div>
                                            <div className="event-details-days-quantity">
                                                <InputNumber
                                                    value={groupedModel.Quantity}
                                                    min={0}
                                                    max={99}
                                                    step={0.5}
                                                    onChange={(value) =>
                                                        handleChangeGroupModelJobEventsQuantity(
                                                            value,
                                                            index,
                                                            groupedModel
                                                        )
                                                    }
                                                />{' '}
                                                days
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Days/HalfDays mode - individual dates */}
                            {(typeQuantity === 'Days' || typeQuantity === 'HalfDays') && !groupWorkDays && (
                                <div className="model">
                                    <div className="model-name-wrapper">
                                        <div className="model-name">
                                            <ModelWithPreviousEvents
                                                model={groupedModel}
                                                isCancelled={groupedModel.AlwaysCancelled}
                                                isConfirmed={groupedModel.HasConfirmation}
                                            />
                                        </div>
                                        <div className="all-selectors">
                                            <NwLinkButton
                                                size="small"
                                                onClick={() => activateAllModel(groupedModel.ModelID)}
                                                label="select all"
                                            />
                                            <Divider type="vertical" />
                                            <NwLinkButton
                                                size="small"
                                                onClick={() => deactivateAllModel(groupedModel.ModelID)}
                                                label="deselect all"
                                            />
                                        </div>
                                    </div>
                                    {modelDates.map((modelDate) => {
                                        if (shouldHideDate(modelDate)) return null;
                                        return (
                                            <div
                                                className="model-date"
                                                key={`${groupedModel.ModelID}-${modelDate.EventDateID}`}
                                            >
                                                <Switch
                                                    checked={modelDate.Active}
                                                    onChange={(value) =>
                                                        handleChangeModelEventDate(value, index, modelDate)
                                                    }
                                                />
                                                <div className="event-details">
                                                    <div
                                                        className={`event-details-name${modelDate.Cancel ? ' is-cancelled' : ''}`}
                                                    >
                                                        <ModelEventTypeTag event={modelDate} />
                                                        <div>{getEventName(modelDate.EventName)}</div>
                                                    </div>
                                                    <div className="event-details-date">
                                                        {showDateNoTime(modelDate.FromDate)}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Hours mode */}
                            {typeQuantity === 'Hours' && (
                                <div className="model">
                                    <div className="model-name-wrapper">
                                        <div className="model-name">
                                            <ModelWithPreviousEvents
                                                model={groupedModel}
                                                isCancelled={groupedModel.AlwaysCancelled}
                                                isConfirmed={groupedModel.HasConfirmation}
                                            />
                                        </div>
                                        <div className="all-selectors">
                                            <NwLinkButton
                                                size="small"
                                                onClick={() => activateAllModel(groupedModel.ModelID)}
                                                label="select all"
                                            />
                                            <Divider type="vertical" />
                                            <NwLinkButton
                                                size="small"
                                                onClick={() => deactivateAllModel(groupedModel.ModelID)}
                                                label="deselect all"
                                            />
                                        </div>
                                    </div>
                                    {modelDates.map((modelDate) => {
                                        if (shouldHideDate(modelDate)) return null;
                                        return (
                                            <div
                                                className="model-date"
                                                key={`${groupedModel.ModelID}-${modelDate.EventDateID}`}
                                            >
                                                <Switch
                                                    checked={modelDate.Active}
                                                    onChange={(value) =>
                                                        handleChangeModelEventDate(value, index, modelDate)
                                                    }
                                                />
                                                <div className="event-details">
                                                    <div
                                                        className={`event-details-name${modelDate.Cancel ? ' is-cancelled' : ''}`}
                                                    >
                                                        <ModelEventTypeTag event={modelDate} />
                                                        <div>{getEventName(modelDate.EventName)}</div>
                                                    </div>
                                                    <div className="event-details-date">
                                                        {modelDate.StartBreak && modelDate.EndBreak ? (
                                                            <ShowMultiTimePeriod event={modelDate} />
                                                        ) : (
                                                            showPeriod(modelDate.FromDate, modelDate.ToDate)
                                                        )}
                                                    </div>
                                                    {modelDate.Active && (
                                                        <div className="event-details-hours-quantity">
                                                            <InputNumber
                                                                value={modelDate.Quantity}
                                                                min={0.1}
                                                                max={24}
                                                                step={0.01}
                                                                onChange={(value) =>
                                                                    handleChangeModelEventDateQuantity(
                                                                        value,
                                                                        index,
                                                                        modelDate
                                                                    )
                                                                }
                                                            />{' '}
                                                            hours
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </ModelSelectorContainer>
    );
};

export default ModelSelectorByWorkDays;
